import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '@/services/api/services/authService';
import { userService } from '@/services/api/services/userService';
import PRtrack_F0F0F0 from '../../assets/PRtrack_F0F0F0.png';
import { User, Lock, Mail, ArrowLeft } from 'lucide-react';
import { ApiResponse } from '@/types/api.types';
import { AuthResponse } from '@/types/user.types';
import { RequiredPasswordChangeModal } from '@/components/features/auth/RequiredPasswordChangeModal';
import { SessionExpiredModal } from '@/components/ui/SessionExpiredModal';
import { getBrowserIanaTimezone } from '@/utils/browserTimezone';

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showRequiredPasswordChange, setShowRequiredPasswordChange] = useState(false);
  const [currentUser, setCurrentUser] = useState<AuthResponse['user'] | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  // Check for session expired message from navigation state
  useEffect(() => {
    console.log('🔍 Login - checking location state:', location.state);
    console.log('🔍 Login - showSessionExpiredModal:', showSessionExpiredModal);
    if (location.state?.message) {
      console.log('🔍 Login - found message, setting modal to true');
      setShowSessionExpiredModal(true);
      // Clear the state to prevent showing the message again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowSessionExpiredModal(false);
    setIsLoading(true);
    try {
      const response: ApiResponse<AuthResponse> = await authService.login({
        username,
        password,
        clientTimezone: getBrowserIanaTimezone(),
      });
      if (response.success) {
        // Check if user requires password change
        if (response.data.user.requiresPasswordChange) {
          setCurrentUser(response.data.user);
          setShowRequiredPasswordChange(true);
          setIsLoading(false);
          return;
        }

        // Normal login flow
        if (response.data.refreshToken) {
          login(response.data.token, response.data.refreshToken, response.data.user);
        } else {
          login(response.data.token, '', response.data.user);
        }
        navigate('/menu');
      } else {
        setError(t(`service.errors.${response.error.code}`));
      }
    } catch {
      setError(t('errors.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.requestPasswordReset({ email: resetEmail });
      if (response.success) {
        setResetSuccess(true);
      } else {
        // Handle specific error codes
        const errorCode = response.error.code;
        if (errorCode === 'REQUEST_PASSWORD_RESET_FAILED') {
          setError(t('service.errors.REQUEST_PASSWORD_RESET_FAILED'));
        } else if (errorCode === 'INVALID_EMAIL') {
          setError(t('service.errors.INVALID_EMAIL'));
        } else if (errorCode === 'USER_NOT_FOUND') {
          setError(t('service.errors.USER_NOT_FOUND'));
        } else {
          setError(response.error.message || t('errors.resetPasswordFailed'));
        }
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      setError(t('errors.resetPasswordFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowResetForm(false);
    setResetEmail('');
    setResetSuccess(false);
    setError(null);
    setShowSessionExpiredModal(false);
  };

  const handleRequiredPasswordChange = async (newPassword: string) => {
    if (!currentUser?.id) {
      setError(t('errors.changePasswordFailed'));
      return;
    }

    setIsChangingPassword(true);
    setError(null);

    try {
      const response = await userService.manualPasswordReset(currentUser.id, newPassword);

      if (response.success) {
        // Close the modal and proceed with normal login
        setShowRequiredPasswordChange(false);
        setCurrentUser(null);

        // Re-login with the new password to get updated user data
        const loginResponse = await authService.login({
          username,
          password: newPassword,
          clientTimezone: getBrowserIanaTimezone(),
        });
        if (loginResponse.success) {
          if (loginResponse.data.refreshToken) {
            login(
              loginResponse.data.token,
              loginResponse.data.refreshToken,
              loginResponse.data.user
            );
          } else {
            login(loginResponse.data.token, '', loginResponse.data.user);
          }
          navigate('/menu');
        } else {
          setError(t(`service.errors.${loginResponse.error.code}`));
        }
      } else {
        setError(response.error.message || t('errors.changePasswordFailed'));
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setError(t('errors.changePasswordFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-full max-w-md rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header negro */}
        <div className="bg-black px-8 pt-8 pb-4 flex flex-col items-center">
          <img src={PRtrack_F0F0F0} alt="PR Track Logo" className="h-20 w-auto mb-2" />
          {/* <h2 className="text-2xl font-extrabold text-white text-center tracking-tight">
            {t('common.appTitle')}
          </h2> */}
        </div>
        {/* Formulario sobre fondo blanco */}
        <div className="bg-white px-8 py-8">
          {!showResetForm ? (
            // Formulario de Login
            <>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t('pages.login.username')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="w-5 h-5 text-gray-400" />
                    </span>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      autoComplete="username"
                      className="login-input block w-full rounded-md border border-gray-300 px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-gray-900 bg-white"
                      placeholder={t('pages.login.username')}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t('pages.login.password')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="w-5 h-5 text-gray-400" />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      className="login-input block w-full rounded-md border border-gray-300 px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-gray-900 bg-white"
                      placeholder={t('pages.login.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2 px-4 rounded-md text-white bg-brand-orange hover:bg-orange-600 font-semibold text-base transition-colors duration-200 disabled:opacity-60 mt-2 flex items-center justify-center gap-2"
                >
                  {isLoading && (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {isLoading ? t('common.signingIn') : t('pages.login.signIn')}
                </button>
                {/* Mensaje de error debajo del botón */}
                {error && (
                  <div
                    className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded text-center text-sm"
                    role="alert"
                  >
                    {t(error) || t('errors.loginFailed')}
                  </div>
                )}
              </form>
              {/* Enlace para reset de contraseña */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setShowResetForm(true);
                  }}
                  className="text-brand-orange hover:text-orange-600 text-sm font-medium transition-colors duration-200"
                >
                  {t('pages.login.forgotPassword')}
                </button>
              </div>

              {/* Test button for session expired modal */}
              {/* TODO: Remove this button */}
              {/* 
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => setShowSessionExpiredModal(true)}
                  className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
                >
                  Test Session Expired Modal
                </button>
              </div>
              */}
            </>
          ) : (
            // Formulario de Reset de Contraseña
            <>
              {!resetSuccess ? (
                <form className="space-y-5" onSubmit={handleResetPassword}>
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('pages.login.resetPasswordTitle')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('pages.login.resetPasswordDescription')}
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="resetEmail"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t('pages.login.email')}
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5 text-gray-400" />
                      </span>
                      <input
                        id="resetEmail"
                        name="resetEmail"
                        type="email"
                        required
                        autoComplete="email"
                        className="login-input block w-full rounded-md border border-gray-300 px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange text-gray-900 bg-white"
                        placeholder={t('pages.login.emailPlaceholder')}
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2 px-4 rounded-md text-white bg-brand-orange hover:bg-orange-600 font-semibold text-base transition-colors duration-200 disabled:opacity-60 mt-2 flex items-center justify-center gap-2"
                  >
                    {isLoading && (
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                    {isLoading ? t('pages.login.sendingReset') : t('pages.login.sendResetLink')}
                  </button>
                  {/* Mensaje de error */}
                  {error && (
                    <div
                      className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded text-center text-sm"
                      role="alert"
                    >
                      {t(error) || t('errors.resetPasswordFailed')}
                    </div>
                  )}
                </form>
              ) : (
                // Mensaje de éxito
                <div className="text-center space-y-4">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('pages.login.resetEmailSent')}
                  </h3>
                  <p className="text-sm text-gray-600">{t('pages.login.resetEmailInstructions')}</p>
                </div>
              )}
              {/* Botón para volver al login */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('pages.login.backToLogin')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Required Password Change Modal */}
      <RequiredPasswordChangeModal
        isOpen={showRequiredPasswordChange}
        onConfirm={handleRequiredPasswordChange}
        isLoading={isChangingPassword}
        userName={currentUser?.name}
      />

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />
    </div>
  );
};
